import { useEffect, useRef, useState } from "react";
import { Mic, Square, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

/**
 * Round mic button. Hold-and-release or click-to-toggle.
 * Records with MediaRecorder → POSTs to /api/todos/voice as multipart form.
 */
export function VoiceRecorder({ onSaved }) {
  const [state, setState] = useState("idle"); // idle | recording | uploading
  const [elapsed, setElapsed] = useState(0);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => () => stopStream(), []);

  const stopStream = () => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  };

  const start = async () => {
    if (state !== "idle") return;
    if (!navigator.mediaDevices || !window.MediaRecorder) {
      toast.error("Voice not supported in this browser");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "";
      const mr = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = handleStop;
      mr.start();
      mediaRecorderRef.current = mr;
      setElapsed(0);
      timerRef.current = setInterval(() => setElapsed((s) => s + 1), 1000);
      setState("recording");
    } catch (err) {
      toast.error("Mic permission denied");
      stopStream();
    }
  };

  const stop = () => {
    if (state !== "recording") return;
    try { mediaRecorderRef.current?.stop(); } catch {}
  };

  const handleStop = async () => {
    setState("uploading");
    const blob = new Blob(chunksRef.current, { type: chunksRef.current[0]?.type || "audio/webm" });
    stopStream();
    if (blob.size < 400) {
      toast.error("Recording too short");
      setState("idle");
      return;
    }
    try {
      const fd = new FormData();
      const ext = blob.type.includes("webm") ? "webm" : "wav";
      fd.append("file", blob, `voice.${ext}`);
      const { data } = await api.post("/todos/voice", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const count = (data?.todos || []).length;
      toast.success(count > 1 ? `Saved ${count} todos` : "Todo saved");
      onSaved?.(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Transcription failed");
    } finally {
      setState("idle");
    }
  };

  const isRecording = state === "recording";
  const isUploading = state === "uploading";

  return (
    <button
      type="button"
      onClick={isRecording ? stop : start}
      disabled={isUploading}
      data-testid="voice-recorder-btn"
      aria-label={isRecording ? "Stop recording" : "Start voice note"}
      className={`relative h-14 w-14 rounded-full grid place-items-center transition-all shadow-md ${
        isRecording
          ? "bg-[#C17D6E] hover:bg-[#A66558] animate-pulse ring-4 ring-[#F7E9E5]"
          : isUploading
          ? "bg-[#8A847A]"
          : "bg-[#4A6B5D] hover:bg-[#3E594D] hover:scale-105"
      }`}
    >
      {isUploading ? (
        <Loader2 className="h-6 w-6 text-white animate-spin" />
      ) : isRecording ? (
        <Square className="h-5 w-5 text-white fill-white" />
      ) : (
        <Mic className="h-6 w-6 text-white" />
      )}
      {isRecording && (
        <span className="absolute -bottom-6 left-1/2 -translate-x-1/2 text-[11px] font-mono text-[#C17D6E] whitespace-nowrap">
          {Math.floor(elapsed / 60)}:{String(elapsed % 60).padStart(2, "0")}
        </span>
      )}
    </button>
  );
}
